import { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FiMoreVertical } from 'react-icons/fi';
import requestStore from '../../stores/requestStore';
import { observer } from 'mobx-react-lite';
import { FaRegTrashCan } from 'react-icons/fa6';
import { FiEdit } from 'react-icons/fi';
import { FaArrowRight } from 'react-icons/fa';
import { FaArrowLeft } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';


import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Typography,
  useTheme,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import AddNewRequest from './AddNewRequest';
import RequestCard from './RequestCard';
import logger from '../../utils/logger';
import ArchivedRequests from '../object/ArchivedRequests';
import ColorTokenPicker from './ColorTokenPicker';
import { kanbanSystemColumnLabelStyles, requestColorStyles } from '../../theme/requestColorTokens';
import { canManageStructure, canMutateResource } from '../../tenancy/resourcePermissions';
import { isNormalKanbanColumn } from '../../utils/kanbanCreation';
import '../../theme/kanbanScrollbar.css';

const KanbanBoard = observer(({ boardId, openArchived, onCloseArchived }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const boardColumns = requestStore.getBoardsByIdArray(boardId);
  const board = requestStore.getBoardById(boardId);
  const mayManageStructure = canManageStructure(board);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  /* logger.log(`boardColumns: ${JSON.stringify(boardColumns)}`)  */

  const rerender = JSON.stringify(boardColumns);
  logger.log(' 🎨 RERENDER');

  // --- Меню и модалки ---
  const [menuOpenForColId, setMenuOpenForColId] = useState(null); // ID колонки, у которой открыто меню

  // Переименование
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTargetColId, setRenameTargetColId] = useState(null);
  const [newColumnName, setNewColumnName] = useState('');
  const [columnName, setColumnName] = useState(''); // Состояние для имени колонки
  const [isAdding, setIsAdding] = useState(false); // Состояние для отображения поля

  // Удаление
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeTargetColId, setRemoveTargetColId] = useState(null);

  const [open, setOpen] = useState(false);
  const [createTargetColumnId, setCreateTargetColumnId] = useState(null);

  const handleOpen = (targetColumnId) => {
    setCreateTargetColumnId(targetColumnId);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setCreateTargetColumnId(null);
  };

  // Заглушки (имитируем запросы на сервер или другие действия)
  function renameColumnStub(columnId, newName) {
    requestStore.renameColumn(columnId, newName, boardId);
  }
  function removeColumnStub(columnId) {
    logger.log('REMOVE COLUMN -> id:', columnId);
    requestStore.removeColumn(boardId, columnId);
  }

  const handleAddColumn = () => {
    if (!mayManageStructure) return;
    setIsAdding(false);
    requestStore.addColumn(boardId, columnName);
    setColumnName('');
  };

  const handleCancel = () => {
    setIsAdding(false);
    setColumnName('');
  };

  const toggleMenu = (event, columnId) => {
    if (!mayManageStructure) return;
    setMenuAnchorEl(event.currentTarget); // Устанавливаем anchorEl
    setMenuOpenForColId(columnId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuOpenForColId(null);
  };

  const handleAddClick = () => {
    if (!mayManageStructure) return;
    setIsAdding(true);
  };

  // --- Переименование колонки ---
  const openRenameDialog = (columnId, currentName = '') => {
    setRenameModalOpen(true);
    setRenameTargetColId(columnId);
    setNewColumnName(currentName);
    setMenuOpenForColId(null); // Закроем меню
  };

  const handleRenameConfirm = () => {
    renameColumnStub(renameTargetColId, newColumnName);
    setRenameModalOpen(false);
    setRenameTargetColId(null);
    setNewColumnName('');
  };

  // --- Удаление колонки ---
  const openRemoveDialog = (columnId) => {
    setRemoveModalOpen(true);
    setRemoveTargetColId(columnId);
    setMenuOpenForColId(null); // Закрываем меню
  };

  const handleRemoveConfirm = () => {
    removeColumnStub(removeTargetColId);
    setRemoveModalOpen(false);
    setRemoveTargetColId(null);
  };

  // --- Состояния и Ref для Drag-to-Scroll ---
  const scrollContainerRef = useRef(null); // Ref для контейнера с колонками
  const [isDraggingScroll, setIsDraggingScroll] = useState(false); // Флаг для drag-to-scroll
  const [startX, setStartX] = useState(0);
  const [scrollLeftStart, setScrollLeftStart] = useState(0);

  const showOperationError = (error) => {
    setErrorMessage(error.response?.data?.error || error.message || 'Не удалось изменить доску')
    setErrorSnackbarOpen(true)
  }

  const handleDragEnd = (result) => {
    // Игнорируем стандартный DND, если мы делали drag-to-scroll
    if (isDraggingScroll) return;

    const { source, destination, type } = result;
    if (!destination) return;

    if (type === 'COLUMN' && mayManageStructure) {
      requestStore.updateColumnOrder(boardId, source.index, destination.index).catch(showOperationError);
    }

    if (type === 'TASK') {
      const request = boardColumns
        .flatMap(column => column.requests || [])
        .find(item => String(item.requestId) === String(result.draggableId));
      if (!canMutateResource(request)) return;
      requestStore.updateTaskOrder(
        boardId,
        parseInt(source.droppableId, 10),
        parseInt(destination.droppableId, 10),
        source.index,
        destination.index,
      ).catch(showOperationError);
    }
  };

  // --- Обработчики для Drag-to-Scroll ---
  const handleMouseDown = (e) => {
    // Проверяем, что клик был НЕ на Draggable элементе (колонка/карточка) или его дочерних элементах
    // Также игнорируем клики на кнопках, инпутах, меню и т.д. внутри области прокрутки
    if (
      e.target.closest('[data-rbd-draggable-id]') ||
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('[role="menuitem"]') ||
      e.target.closest('[role="menu"]') ||
      e.target.closest('[role="dialog"]') ||
      e.target.closest('a') // Добавим ссылки на всякий случай
    ) {
      return; // Игнорируем клик
    }

    // Проверка, что клик не на самой полосе прокрутки
    const scrollbarWidth =
      scrollContainerRef.current.offsetWidth - scrollContainerRef.current.clientWidth;
    const scrollbarHeight =
      scrollContainerRef.current.offsetHeight - scrollContainerRef.current.clientHeight;
    if (
      e.nativeEvent.offsetX >= scrollContainerRef.current.clientWidth || // Клик правее видимой области (вертикальный скролл или пустое место)
      e.nativeEvent.offsetY >= scrollContainerRef.current.clientHeight
    ) {
      // Клик ниже видимой области (горизонтальный скролл)
      // Дополнительная проверка на реальную ширину/высоту скроллбара
      if (
        e.nativeEvent.offsetX >= scrollContainerRef.current.offsetWidth - scrollbarWidth &&
        e.nativeEvent.offsetY >= scrollContainerRef.current.offsetHeight - scrollbarHeight
      ) {
        return; // Похоже на клик на пересечении скроллбаров или рядом
      }
      if (
        e.nativeEvent.offsetX >= scrollContainerRef.current.clientWidth &&
        scrollContainerRef.current.clientHeight < scrollContainerRef.current.offsetHeight
      ) {
        // Не уверен, что это лучший способ определить клик на вертикальном скроллбаре
        // return;
      }
      if (
        e.nativeEvent.offsetY >= scrollContainerRef.current.clientHeight &&
        scrollContainerRef.current.clientWidth < scrollContainerRef.current.offsetWidth
      ) {
        return; // Клик на горизонтальном скроллбаре
      }
    }

    // Начинаем drag-to-scroll
    setIsDraggingScroll(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeftStart(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing'; // Курсор "схвачено"
    scrollContainerRef.current.style.userSelect = 'none'; // Запретить выделение текста
  };

  const handleMouseLeaveOrUp = () => {
    // Завершаем drag-to-scroll
    if (isDraggingScroll && scrollContainerRef.current) {
      setIsDraggingScroll(false);
      scrollContainerRef.current.style.cursor = 'grab'; // Курсор "схватить"
      scrollContainerRef.current.style.userSelect = ''; // Разрешить выделение
    }
  };

  const handleMouseMove = (e) => {
    if (!isDraggingScroll || !scrollContainerRef.current) return;
    e.preventDefault(); // Предотвратить выделение текста
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Умножаем на 1.5 для ускорения прокрутки
    scrollContainerRef.current.scrollLeft = scrollLeftStart - walk;
  };

  // Используем useEffect для добавления/удаления глобального слушателя mouseup,
  // чтобы корректно завершить перетаскивание, если кнопка мыши отпущена вне контейнера
  useEffect(() => {
    const handleGlobalMouseUp = (e) => {
      // Проверяем, был ли mouseup НЕ на scrollContainerRef, но перетаскивание было активно
      if (isDraggingScroll) {
        handleMouseLeaveOrUp();
      }
    };

    if (isDraggingScroll) {
      // Добавляем слушатель на весь документ
      document.addEventListener('mouseup', handleGlobalMouseUp);
    } else {
      // Удаляем слушатель, если не перетаскиваем
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    }

    // Очистка при размонтировании компонента
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingScroll]); // Зависимость от флага перетаскивания

  // функция сохранения заявки
  const handleSave = async (boardId, columnId, title, description, addressShort, sourceId, clientSelection) => {
    logger.log('сработал handleSave из компонента KanbanBoard');

    // Проверяем, выбран ли адрес из подсказок и не пуст ли объект адреса
    /* 		if (Object.keys(requestStore.address).length === 0) {
			alert('Адрес не должен быть пустым')
			return
		} */
    if (Object.keys(requestStore.address).length === 0) {
      alert('Адрес должен быть корректно заполнен');
      return;
    }

    setOpen(false); // Закрыть модальное окно после сохранения
    
    try {
      const result = await requestStore.createRequest(boardId, columnId, title, description, addressShort, sourceId, clientSelection);
      
      if (result && result.success) {
        setSnackbarOpen(true); // Показать уведомление об успехе
      } else if (result) {
        // Показываем сообщение об ошибке
        setErrorMessage(result.error);
        setErrorSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Ошибка при создании заявки:', error);
      setErrorMessage('Произошла ошибка при создании заявки');
      setErrorSnackbarOpen(true);
    }
  };
  // Функция для перемещения колонки
  const handleMoveColumn = (columnId, direction) => {
    logger.log('сработал handleMoveColumn из компонента KanbanBoard');

    if (direction === 'left') {
      logger.log('влево');
      requestStore.moveColumnLeft(boardId, columnId).catch(showOperationError);
    } else if (direction === 'right') {
      logger.log('вправо');
      requestStore.moveColumnRight(boardId, columnId).catch(showOperationError);
    }
    setMenuOpenForColId(null);
  };

  // Обработчик для кружочков
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorSnackbarOpen(false);
  };

  return (
    <div className="h-full bg-background p-4 flex flex-col overflow-hidden select-none">
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && (
        <div onClick={() => logger.log('KanbanBoard Render')}>% KanbanBoard %</div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Заявка сохранена
        </Alert>
      </Snackbar>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ALL_COLUMNS" direction="horizontal" type="COLUMN">
          {(provided) => (
            <div
              ref={(node) => {
                provided.innerRef(node);
                scrollContainerRef.current = node;
              }}
              {...provided.droppableProps}
              className="reldo-kanban-scrollbar flex-grow overflow-x-auto overflow-y-hidden flex items-start gap-4 pb-2"
              style={{ cursor: isDraggingScroll ? 'grabbing' : 'grab' }}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeaveOrUp}
              onMouseUp={handleMouseLeaveOrUp}
              onMouseMove={handleMouseMove}>
              {boardColumns
                .slice()
                .sort((a, b) => a.sort - b.sort)
                .map((column, colIndex) => {
                  const columnColors = requestColorStyles(column.colorToken, theme.palette.mode);
                  const systemLabelColors = kanbanSystemColumnLabelStyles(column.systemType, theme.palette.mode);
                  return (
                  <Draggable
                    key={column.columnId}
                    draggableId={`${column.columnId}`}
                    index={colIndex}
                    isDragDisabled={isDraggingScroll || !mayManageStructure}>
                    {(provided) => (
                      <div
                        className="rounded-lg shadow-sm p-3 w-[300px] flex-shrink-0 flex flex-col h-full"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          backgroundColor: columnColors?.background || (isDarkMode ? '#303030' : '#e7e7e7'),
                          color: columnColors?.text,
                          border: columnColors ? `1px solid ${columnColors.border}` : undefined,
                        }}>
                        <div
                          className="flex justify-between items-center mb-3 flex-shrink-0"
                          {...provided.dragHandleProps}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <h2
                              className="text-md font-semibold truncate"
                              style={systemLabelColors ? {
                                backgroundColor: systemLabelColors.background,
                                border: `1px solid ${systemLabelColors.border}`,
                                borderRadius: '4px',
                                color: systemLabelColors.text,
                                padding: '4px 10px',
                              } : undefined}>
                              {column.name}
                            </h2>
                          </Box>
                          {mayManageStructure && <IconButton
                            size="small"
                            onClick={(event) => toggleMenu(event, column.columnId)}
                            sx={{ padding: '4px' }}>
                            <FiMoreVertical fontSize="small" />
                          </IconButton>}
                          {mayManageStructure && <Menu
                            anchorEl={menuAnchorEl}
                            open={menuOpenForColId === column.columnId}
                            onClose={handleMenuClose}>
                            <MenuItem
                              onClick={() => openRenameDialog(column.columnId, column.name)}>
                              <FiEdit size={14} style={{ marginRight: '8px' }} /> Переименовать
                            </MenuItem>
                            <ColorTokenPicker
                              value={column.colorToken}
                              onChange={(colorToken) => requestStore.updateColumnColor(column.columnId, colorToken)}
                            />
                            <div className="text-xs text-center text-gray-500 mt-1 mb-1">
                              Переместить
                            </div>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, px: 1 }}>
                              <IconButton
                                onClick={() => handleMoveColumn(column.columnId, 'left')}
                                size="small"
                                disabled={colIndex === 0}
                                title="Влево">
                                <FaArrowLeft size={12} />
                              </IconButton>
                              <IconButton
                                onClick={() => handleMoveColumn(column.columnId, 'right')}
                                size="small"
                                disabled={colIndex === boardColumns.length - 1}
                                title="Вправо">
                                <FaArrowRight size={12} />
                              </IconButton>
                            </Box>
                            <Box sx={{ my: 0.5, mx: 1, height: '1px', bgcolor: 'divider' }} />
                            {column.systemType === 'NORMAL' && column.requests.length === 0 && (
                              <MenuItem onClick={() => openRemoveDialog(column.columnId)}>
                                <FaRegTrashCan
                                  size={14}
                                  style={{ marginRight: '8px', color: '#CC0000' }}
                                />
                                Удалить колонку
                              </MenuItem>
                            )}
                          </Menu>}
                        </div>

                        <Droppable droppableId={column.columnId.toString()} type="TASK">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="reldo-kanban-scrollbar min-h-0 flex-grow overflow-y-auto overflow-x-hidden pr-1 space-y-2">
                              {column.requests.map((request, index) => (
                                <Draggable
                                  key={request.requestId}
                                  draggableId={request.requestId.toString()}
                                  index={index}
                                  isDragDisabled={isDraggingScroll || !canMutateResource(request)}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}>
                                      <RequestCard
                                        title={request.title}
                                        description={request.description}
                                        id={request.requestId}
                                        assigneeMembershipId={request.assigneeMembershipId}
                                        permissions={request.permissions}
                                        columnId={column.columnId}
                                        boardId={boardId}
                                        colorToken={request.colorToken}
                                        addressShort={request.filter.addressShort}
                                        extraFilter={request.filter.extraFilter}
                                        jsonFileName={request.filter.jsonFileName}
                                        countryCodeISO={request.filter.countryCodeISO}
                                        uuid={request.uuid}
                                        outcome={request.outcome}
                                        closedAt={request.closedAt}
                                        sourceId={request.sourceId}
                                        sourceName={request.sourceName}
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                        {isNormalKanbanColumn(column) && (
                          <Box sx={{ flexShrink: 0, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Button
                              size="small"
                              variant="text"
                              color="inherit"
                              onClick={() => handleOpen(column.columnId)}
                              aria-label={`Добавить заявку в колонку ${column.name}`}
                              sx={{
                                justifyContent: 'flex-start',
                                minHeight: 32,
                                px: 1,
                                color: 'text.secondary',
                                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                              }}>
                              + Добавить заявку
                            </Button>
                          </Box>
                        )}
                      </div>
                    )}
                  </Draggable>
                  );
                })}
              {provided.placeholder}
              <div className="ml-2 flex-shrink-0 self-start pt-1">
                {mayManageStructure && (!isAdding ? (
                  <Button variant="text" size="small" onClick={handleAddClick}>
                    + Добавить колонку
                  </Button>
                ) : (
                  <Paper elevation={2} sx={{ p: 2, width: 280 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Имя колонки"
                      value={columnName}
                      onChange={(e) => setColumnName(e.target.value)}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button variant="outlined" size="small" onClick={handleCancel}>
                        Отмена
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddColumn}
                        disabled={!columnName.trim()}>
                        Добавить
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {renameModalOpen && (
        <Modal
          open={renameModalOpen}
          onClose={() => setRenameModalOpen(false)}
          aria-labelledby="rename-modal-title"
          aria-describedby="rename-modal-description">
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}>
            <Typography id="rename-modal-title" variant="h6" component="h2" gutterBottom>
              Переименовать колонку
            </Typography>
            <TextField
              fullWidth
              label="Новое имя колонки"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => setRenameModalOpen(false)}>
                Отмена
              </Button>
              <Button variant="contained" color="primary" onClick={handleRenameConfirm}>
                Сохранить
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {removeModalOpen && (
        <Modal
          open={removeModalOpen}
          onClose={() => setRemoveModalOpen(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: '8px',
            }}>
            <Typography id="modal-modal-title" variant="h6" component="h2" mb={2}>
              Удалить колонку?
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4, color: 'text.secondary' }}>
              Вы действительно хотите удалить эту колонку?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={() => setRemoveModalOpen(false)}>
                Отмена
              </Button>
              <Button variant="contained" color="error" onClick={handleRemoveConfirm}>
                Удалить
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translate(-50%, 0)',
            width: '97%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 1,
            maxHeight: '95vh',
            overflowY: 'auto',

            [theme.breakpoints.up('sm')]: {
              width: '90%',
            },
            [theme.breakpoints.up('md')]: {
              width: '80%',
            },
            [theme.breakpoints.up('lg')]: {
              width: '80%',
            },
            [theme.breakpoints.up('xl')]: {
              width: '50%',
            },
          })}>
          <AddNewRequest onSave={handleSave} onCancel={handleClose} boardId={boardId} targetColumnId={createTargetColumnId} />
        </Box>
      </Modal>
      
     

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleErrorSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleErrorSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Archived Requests Modal */}
      <Modal
        open={openArchived}
        onClose={onCloseArchived}
        aria-labelledby="archived-requests-modal-title"
        aria-describedby="archived-requests-modal-description"
      >
        <Box sx={(theme) => ({
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '600px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 0,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          [theme.breakpoints.up('md')]: {
             maxWidth: '800px',
          },
          [theme.breakpoints.up('lg')]: {
            maxWidth: '1000px',
          },
        })}>
           <IconButton
             aria-label="Закрыть"
             onClick={onCloseArchived}
             sx={{
               position: 'absolute',
               right: 8,
               top: 8,
               color: (theme) => theme.palette.grey[500],
             }}
           >
             <MdClose />
           </IconButton>
          <Typography id="archived-requests-modal-title" variant="h6" component="h2" sx={{ p: 2, pb: 1 }}>
            Архивные заявки
          </Typography>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            <Paper elevation={0} sx={{ 
               background: 'transparent'
              }}>
              <ArchivedRequests />
            </Paper>
          </Box>
        </Box>
      </Modal>
    </div>
  );
});

export default KanbanBoard;
